<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import {
    AreaSeries,
    type IChartApi,
    type ISeriesApi,
    type Time,
    type UTCTimestamp,
    createChart,
  } from 'lightweight-charts';
  import type { EquityCurvePoint } from '$lib/api/sessions';
  import { themeStore } from '$lib/stores/theme.svelte';
  import {
    type ChartTheme,
    getChartTheme,
    isoToUtcSeconds,
    tickMarkFormatterET,
    withAlpha,
  } from '$lib/utils/chart-helpers';

  // ============================================================
  // <EquityCurveChart>  -  Fase 5.
  //
  // Reemplazo del Plotly equity curve del v1 Portfolio Overview.
  //
  // Mejoras vs v1:
  //  - Eje X temporal real (exitTime de cada trade) en lugar de index discreto.
  //  - Area fill (verde si positivo, rojo si negativo) bajo la curva.
  //  - Zero line dashed siempre visible.
  //  - Hover muestra fecha + cumulative PnL (no solo "Trade #N").
  //  - Theme dinamico (lee CSS vars).
  // ============================================================

  interface Props {
    points: EquityCurvePoint[];
    height?: number;
  }

  let { points, height = 280 }: Props = $props();

  let container: HTMLDivElement;
  let chart: IChartApi | null = null;
  let series: ISeriesApi<'Area'> | null = null;

  let theme = $state<ChartTheme>({
    background: '#1a1a1a',
    foreground: '#f5f1e8',
    border: '#404040',
    grid: '#404040',
    success: '#4ade80',
    destructive: '#f87171',
    primary: '#D87807',
    gold: '#d4af37',
    muted: '#b0b0b0',
  });

  // ----- Color de la serie segun ultimo valor -----
  let lastValue = $derived(points.length > 0 ? points[points.length - 1]!.cumulativePnL : 0);
  let isPositive = $derived(lastValue >= 0);
  let seriesColor = $derived(isPositive ? theme.success : theme.destructive);

  function getSeriesData() {
    const out: Array<{ time: UTCTimestamp; value: number }> = [];
    const seen = new Set<number>();
    for (const p of points) {
      const t = isoToUtcSeconds(p.exitTime);
      if (t == null) continue;
      // Lightweight rechaza duplicados. Si hay misma exitTime (raro), nos quedamos con el ultimo.
      if (seen.has(t)) {
        // sustituimos el valor del item anterior
        for (let i = out.length - 1; i >= 0; i--) {
          if ((out[i] as { time: UTCTimestamp }).time === t) {
            out[i] = { time: t as UTCTimestamp, value: p.cumulativePnL };
            break;
          }
        }
        continue;
      }
      seen.add(t);
      out.push({ time: t as UTCTimestamp, value: p.cumulativePnL });
    }
    out.sort((a, b) => (a.time as number) - (b.time as number));
    return out;
  }

  // ----- Hover tooltip enriquecido -----
  let tooltipVisible = $state(false);
  let tooltipContent = $state<{ time: string; pnl: number; index: number } | null>(null);
  let tooltipX = $state(0);
  let tooltipY = $state(0);

  function setupCrosshair() {
    if (!chart || !series) return;
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || param.point.x < 0 || param.point.y < 0 || !param.time) {
        tooltipVisible = false;
        return;
      }
      const v = param.seriesData.get(series!) as { value: number } | undefined;
      if (!v) {
        tooltipVisible = false;
        return;
      }
      const t = param.time as number;
      const date = new Date(t * 1000);
      const etFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      // Index aproximado: busqueda binaria
      let idx = -1;
      for (let i = 0; i < points.length; i++) {
        if (isoToUtcSeconds(points[i]!.exitTime) === t) {
          idx = i + 1;
          break;
        }
      }
      tooltipContent = { time: etFmt.format(date), pnl: v.value, index: idx };
      tooltipVisible = true;
      tooltipX = param.point.x + 12;
      tooltipY = param.point.y + 12;
    });
  }

  onMount(() => {
    theme = getChartTheme();
    const initialWidth = container.clientWidth > 0 ? container.clientWidth : 600;
    chart = createChart(container, {
      width: initialWidth,
      height,
      layout: {
        background: { color: 'transparent' },
        textColor: theme.foreground,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      grid: {
        vertLines: { color: theme.grid, visible: false },
        horzLines: { color: theme.grid, style: 0, visible: true },
      },
      rightPriceScale: {
        borderColor: theme.border,
        autoScale: true,
      },
      timeScale: {
        borderColor: theme.border,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          tickMarkFormatterET(time as number, tickMarkType),
      },
      crosshair: { mode: 1 },
      handleScroll: { mouseWheel: false, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
    });

    series = chart.addSeries(AreaSeries, {
      lineColor: seriesColor,
      topColor: withAlpha(seriesColor, 0.33),
      bottomColor: withAlpha(seriesColor, 0),
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    refreshData();
    // Zero line horizontal
    series.createPriceLine({
      price: 0,
      color: theme.muted,
      lineWidth: 1,
      lineStyle: 2, // Dashed
      axisLabelVisible: false,
      title: '',
    });

    setupCrosshair();

    const ro = new ResizeObserver(() => {
      if (chart && container) chart.applyOptions({ width: container.clientWidth });
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      chart?.remove();
      chart = null;
    };
  });

  function refreshData() {
    if (!chart || !series) return;
    // biome-ignore lint/suspicious/noExplicitAny: Lightweight types
    series.setData(getSeriesData() as any);
    series.applyOptions({
      lineColor: seriesColor,
      topColor: withAlpha(seriesColor, 0.33),
      bottomColor: withAlpha(seriesColor, 0),
    });
    chart.timeScale().fitContent();
  }

  $effect(() => {
    void points;
    untrack(() => refreshData());
  });

  $effect(() => {
    void themeStore.theme;
    untrack(() => {
      if (!chart) return;
      theme = getChartTheme();
      chart.applyOptions({
        layout: { background: { color: 'transparent' }, textColor: theme.foreground },
        grid: { horzLines: { color: theme.grid } },
        rightPriceScale: { borderColor: theme.border },
        timeScale: { borderColor: theme.border },
      });
      refreshData();
    });
  });

  function fmtPnL(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }
</script>

<div class="relative w-full" style="height: {height}px;">
  <div bind:this={container} class="absolute inset-0"></div>

  {#if tooltipVisible && tooltipContent}
    <div
      class="pointer-events-none absolute z-10 rounded-lg border border-border bg-surface-3/95 px-3 py-2 shadow-luxury backdrop-blur lifted-edge"
      style="left: {tooltipX}px; top: {tooltipY}px;"
    >
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
        {tooltipContent.time} ET{tooltipContent.index > 0
          ? ` · Trade #${tooltipContent.index}`
          : ''}
      </div>
      <div
        class={[
          'mt-1 font-mono text-base font-semibold tabular-nums',
          tooltipContent.pnl >= 0 ? 'text-success' : 'text-destructive',
        ].join(' ')}
      >
        {fmtPnL(tooltipContent.pnl)}
      </div>
    </div>
  {/if}
</div>
